import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["step", "progress", "progressBar", "backButton", "nextButton", "submitButton", "dot"]
  static values = {
    currentStep: { type: Number, default: 0 }
  }

  connect() {
    this.totalSteps = this.stepTargets.length
    this.showStep(this.currentStepValue)
    this.updateProgress()
    console.log("Multi-step form controller connected with", this.totalSteps, "steps")
  }

  next(event) {
    event?.preventDefault()

    // Validate current step before proceeding
    if (!this.validateCurrentStep()) {
      return
    }

    if (this.currentStepValue < this.totalSteps - 1) {
      this.currentStepValue++
      this.showStep(this.currentStepValue)
      this.updateProgress()
      this.scrollToTop()
    }
  }

  back(event) {
    event?.preventDefault()

    if (this.currentStepValue > 0) {
      this.currentStepValue--
      this.showStep(this.currentStepValue)
      this.updateProgress()
      this.scrollToTop()
    }
  }

  goToStep(event) {
    event.preventDefault()
    const step = parseInt(event.currentTarget.dataset.step)

    // Only allow going to previous steps or current step
    if (step <= this.currentStepValue) {
      this.currentStepValue = step
      this.showStep(this.currentStepValue)
      this.updateProgress()
      this.scrollToTop()
    }
  }

  showStep(stepIndex) {
    this.stepTargets.forEach((step, index) => {
      if (index === stepIndex) {
        step.classList.remove('d-none')
        step.classList.add('fade-in')
        // Remove animation class after animation completes
        setTimeout(() => step.classList.remove('fade-in'), 300)
      } else {
        step.classList.add('d-none')
      }
    })

    // Update navigation buttons
    this.updateNavigationButtons()
  }

  updateNavigationButtons() {
    const isFirstStep = this.currentStepValue === 0
    const isLastStep = this.currentStepValue === this.totalSteps - 1

    // Back button
    if (this.hasBackButtonTarget) {
      if (isFirstStep) {
        this.backButtonTarget.classList.add('invisible')
      } else {
        this.backButtonTarget.classList.remove('invisible')
      }
    }

    // Next/Submit buttons
    if (this.hasNextButtonTarget && this.hasSubmitButtonTarget) {
      if (isLastStep) {
        this.nextButtonTarget.classList.add('d-none')
        this.submitButtonTarget.classList.remove('d-none')
      } else {
        this.nextButtonTarget.classList.remove('d-none')
        this.submitButtonTarget.classList.add('d-none')
      }
    }
  }

  updateProgress() {
    const progress = ((this.currentStepValue + 1) / this.totalSteps) * 100

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${progress}%`
      this.progressBarTarget.setAttribute('aria-valuenow', progress)
    }

    if (this.hasProgressTarget) {
      this.progressTarget.textContent = `${this.currentStepValue + 1} / ${this.totalSteps}`
    }

    // Update step dots
    if (this.hasDotTarget) {
      this.dotTargets.forEach((dot, index) => {
        dot.classList.remove('active', 'completed')
        if (index < this.currentStepValue) {
          dot.classList.add('completed')
        } else if (index === this.currentStepValue) {
          dot.classList.add('active')
        }
      })
    }
  }

  validateCurrentStep() {
    const currentStep = this.stepTargets[this.currentStepValue]
    const requiredInputs = currentStep.querySelectorAll('[required]:not([disabled])')

    let isValid = true
    requiredInputs.forEach(input => {
      // Check if input has value (for text/number inputs)
      if (input.type === 'text' || input.type === 'number' || input.type === 'datetime-local' || input.tagName === 'TEXTAREA') {
        if (!input.value.trim()) {
          isValid = false
          this.showValidationError(input)
        } else {
          this.clearValidationError(input)
        }
      }
      // Check if select has value
      else if (input.tagName === 'SELECT') {
        if (!input.value) {
          isValid = false
          this.showValidationError(input)
        } else {
          this.clearValidationError(input)
        }
      }
      // Check if file input has file
      else if (input.type === 'file') {
        if (input.files.length === 0) {
          isValid = false
          this.showValidationError(input)
        } else {
          this.clearValidationError(input)
        }
      }
    })

    return isValid
  }

  showValidationError(input) {
    input.classList.add('is-invalid')

    // Add shake animation
    input.classList.add('shake')
    setTimeout(() => input.classList.remove('shake'), 500)
  }

  clearValidationError(input) {
    input.classList.remove('is-invalid')
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Skip validation for optional steps
  skipStep(event) {
    event?.preventDefault()
    console.log("Skipping step", this.currentStepValue)

    if (this.currentStepValue < this.totalSteps - 1) {
      this.currentStepValue++
      this.showStep(this.currentStepValue)
      this.updateProgress()
      this.scrollToTop()
      console.log("Moved to step", this.currentStepValue)
    }
  }
}
